import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Dropdown, Flex, Modal } from 'antd'
import {
  DownOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import { getRequest, postRequest } from '../../services/base'
import { useParams } from 'react-router-dom'
import { NavContext } from '../../context/global_context'
import { downloadErrorLogs } from '../../utils/common'
import { Success, Warning } from './helperComponents'

const MissingLogOption = {
  value: 'log_missing_components',
  label: 'Log missing components',
}

const DropdownActions = ({ fileType, uploadState, reloadData }) => {
  const [navState, dispatch] = useContext(NavContext)
  const [modal, contextHolder] = Modal.useModal()

  const { vehicle } = useParams()

  const selectedVersion = uploadState?.selected_version
  const disabled = uploadState?.file_type !== fileType

  const selected = selectedVersion?.value

  const importFileId = selected && selected !== 'all' ? selectedVersion?.id : ''
  // console.log(fileType, uploadState)

  const disabledDownloadLogs = !(
    fileType === uploadState?.file_type && 
    selectedVersion?.file?.has_error
  ) ? true : false

  const disabledDownload = !(
    fileType === uploadState?.file_type &&
    (selectedVersion?.id ||
      [
        'all',
        'current_state_inlcude_not_constrained',
        'current_state_include_part_number',
      ].includes(selectedVersion?.value)) &&
    (!selectedVersion?.file?.error_reason)
  )

  const disabledRollback = !(
    fileType === uploadState?.file_type && 
    (selectedVersion?.id) && 
    (!selectedVersion?.file?.error_reason)
  )
  
  const downloadPath = async () => {
    dispatch({
      type: 'loader',
      data: {
        caption: 'Downloading requested data',
        showLoader: true,
      },
      notification: {},
    })

    const formData = new FormData()

    formData.append('vehicle_id', vehicle)

    if (selectedVersion?.id) {
      formData.append('importer_id', selectedVersion.id)
    }

    formData.append('file_type', uploadState?.file_type)

    if (!selectedVersion?.value?.id) {
      formData.append('selected_version', selectedVersion?.value)
    }

    const res = await postRequest('/api/importer/export/', formData, true)

    if (res?.msg) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: res.msg,
        },
      })
      return
    }

    const url = window.URL.createObjectURL(new Blob([res]))

    const link = document.createElement('a')

    link.href = url

    if (uploadState?.file_type === 'connector_layout') {
      link.setAttribute(
        'download',
        `${navState.make_model_id}_${vehicle}_${uploadState.file_type}${
          selectedVersion?.value === 'current_state_include_part_number'
            ? '_PN'
            : ''
        }.zip`
      )
    } else {
      link.setAttribute(
        'download',
        `${navState.make_model_id}_${vehicle}_${uploadState.file_name}`
      )
    }

    document.body.appendChild(link)
    link.click()
    link.remove()

    dispatch({
      type: 'notification',
      data: {
        notification_type: 'success',
        msg: 'File downloaded successfully',
        loader: true,
      },
    })
  }

  const downloadLogs = async () => {
    const res = await getRequest(
      `/api/importer/download_logs/?download_id=${selectedVersion?.id}`
    )

    const data = res?.data

    if (data?.error?.length > 0) {
      downloadErrorLogs(data.error)
    } else if (data) {
      downloadErrorLogs([data])
    } else {
      // window.alert('Logs not available.')
      modal.info({
        icon: <ExclamationCircleOutlined />,
        title: 'Info',
        content: (
          <>
            Logs not available.
          </>
        ),
        centered: true,
        maskClosable: true,
        onOk: () => {},
      })
    }
  }

  const downloadMissingComponent = async () => {
    const res = await postRequest('/api/dtcs/missing_component/', {
      vehicle,
      version: importFileId,
    })

    if (res.data.dtcs_review?.length > 0) {
      downloadErrorLogs(res.data.dtcs_review, 'dtc_needs_to_review.txt')
    }

    if (res.data.missing_connector_device?.length > 0) {
      downloadErrorLogs(
        res.data.missing_connector_device,
        'missing_components.txt'
      )
    }

    if (
      res.data.dtcs_review?.length < 1 &&
      res.data.missing_connector_device?.length < 1
    ) {
      window.alert('Logs not available.')
    }
  }

  const confirmRollback = async () => {
    dispatch({
      type: 'loader',
      data: {
        caption: `Deleting #${selectedVersion?.label} Version`,
        showLoader: true,
      },
      notification: {},
    })

    const res = await getRequest(
      `/api/importer/rollback/?vehicle_id=${vehicle}&file_type=${fileType}&version=${selectedVersion?.file?.version}`
    )

    if (res?.error_code) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: res.msg,
          loader: false,
        },
      })
    } else {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: `Deleted #${selectedVersion?.label} version Successfully`,
          loader: false,
        },
      })

      reloadData()
    }
  }

  const showRollbackConfirm = () => {
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Rollback Import Version',
      content: (
        <>
          Are you sure you want to rollback import version #
          {selectedVersion?.label}?
        </>
      ),
      centered: true,
      maskClosable: true,
      okText: 'Rollback',
      cancelText: 'Cancel',
      onOk: confirmRollback,
    })
  }

  const status =
    selected && selected !== 'all' && selectedVersion?.file ? (
      selectedVersion.file.status === 'failed' ? (
        <Warning />
      ) : (
        <Success />
      )
    ) : null

  const tempActionsMenu = [
    {
      value: 'rollback',
      label: 'Rollback',
      disabled: disabledRollback,
    },
    {
      value: 'logs',
      label: 'Logs',
      disabled: disabledDownloadLogs,
      statusSvg: status,
    },
  ]

  const actionsMenu =
    uploadState?.file_type === 'sales_codes' && selectedVersion?.value !== 'all'
      ? tempActionsMenu
      : [
          ...tempActionsMenu,
          {
            value: 'download',
            label: importFileId ? 'Download Diff' : 'Download',
            disabled: disabledDownload,
          },
        ]

  const finalActions =
    uploadState?.file_type !== 'dtcs' || importFileId === ''
      ? actionsMenu
      : [...actionsMenu, MissingLogOption]

  const menuItems = finalActions.map((item) => ({
    key: item.value,
    disabled: item.disabled,
    label: (
      <Flex justify="space-between" align="center">
        <span>{item.label}</span>
        {item.statusSvg}
      </Flex>
    ),
  }))

  const handleMenuClick = ({ key }) => {
    switch (key) {
      case 'download':
        downloadPath()
        break

      case 'logs':
        downloadLogs()
        break

      case 'rollback':
        showRollbackConfirm()
        break

      case 'log_missing_components':
        downloadMissingComponent()
        break

      default:
        break
    }
  }

  return (
    <>
      <Dropdown
        trigger={['click']}
        placement="bottomCenter"
        disabled={disabled}
        overlayClassName="actions-dropdown"
        menu={{
          items: menuItems,
          onClick: handleMenuClick,
        }}
      >
        <Button className="actions-select-trigger">
          <span>Actions</span>
          <DownOutlined />
        </Button>
      </Dropdown>

      {contextHolder}
    </>
  )
}

DropdownActions.propTypes = {
  fileType: PropTypes.string.isRequired,
  uploadState: PropTypes.object,
  reloadData: PropTypes.func.isRequired,
}

export default DropdownActions
